/**
 * Client API Error Handling
 *
 * Centralizes error handling for client-related API operations.
 * Maps API errors to user-friendly messages.
 */

// Error codes for client operations
export const CLIENT_ERROR_CODES = {
  // Generic errors
  UNKNOWN: 'UNKNOWN_ERROR',
  NETWORK: 'NETWORK_ERROR',
  TIMEOUT: 'TIMEOUT_ERROR',

  // Client errors
  CLIENT_NOT_FOUND: 'CLIENT_NOT_FOUND',
  CLIENT_INVALID_ID: 'CLIENT_INVALID_ID',

  // Tag errors
  TAG_NOT_FOUND: 'TAG_NOT_FOUND',
  TAG_INVALID_ID: 'TAG_INVALID_ID',
  TAG_ALREADY_ASSIGNED: 'TAG_ALREADY_ASSIGNED',
  TAG_NOT_ASSIGNED: 'TAG_NOT_ASSIGNED',

  // Relationship errors
  RELATIONSHIP_NOT_FOUND: 'RELATIONSHIP_NOT_FOUND',
  RELATIONSHIP_INVALID: 'RELATIONSHIP_INVALID',

  // Permission errors
  FORBIDDEN: 'FORBIDDEN',
  UNAUTHORIZED: 'UNAUTHORIZED',
};

// User-friendly error messages
const ERROR_MESSAGES = {
  [CLIENT_ERROR_CODES.UNKNOWN]: 'An unexpected error occurred. Please try again.',
  [CLIENT_ERROR_CODES.NETWORK]: 'Unable to connect to the server. Please check your internet connection.',
  [CLIENT_ERROR_CODES.TIMEOUT]: 'The request timed out. Please try again.',

  [CLIENT_ERROR_CODES.CLIENT_NOT_FOUND]: 'Client not found.',
  [CLIENT_ERROR_CODES.CLIENT_INVALID_ID]: 'Invalid client ID provided.',

  [CLIENT_ERROR_CODES.TAG_NOT_FOUND]: 'Tag not found.',
  [CLIENT_ERROR_CODES.TAG_INVALID_ID]: 'Invalid tag ID provided.',
  [CLIENT_ERROR_CODES.TAG_ALREADY_ASSIGNED]: 'This tag is already assigned to the client.',
  [CLIENT_ERROR_CODES.TAG_NOT_ASSIGNED]: 'This tag is not assigned to the client.',

  [CLIENT_ERROR_CODES.RELATIONSHIP_NOT_FOUND]: 'Relationship not found.',
  [CLIENT_ERROR_CODES.RELATIONSHIP_INVALID]: 'Invalid relationship data.',

  [CLIENT_ERROR_CODES.FORBIDDEN]: 'You do not have permission to perform this action.',
  [CLIENT_ERROR_CODES.UNAUTHORIZED]: 'Please log in to continue.',
};

/**
 * Maps HTTP status codes and API error responses to error codes
 * @param {Error} error - Axios error object
 * @returns {string} Error code from CLIENT_ERROR_CODES
 */
const mapErrorToCode = error => {
  // Network errors (no response)
  if (!error.response) {
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return CLIENT_ERROR_CODES.TIMEOUT;
    }
    return CLIENT_ERROR_CODES.NETWORK;
  }

  const { status, data } = error.response;

  // Check for specific API error codes in response
  const apiErrorCode = data?.code || data?.errorCode || data?.error?.code;
  if (apiErrorCode) {
    // Map API-specific error codes
    switch (apiErrorCode.toUpperCase()) {
      case 'CLIENT_NOT_FOUND':
      case 'NOT_FOUND':
        if (error.config?.url?.includes('/clients/')) {
          return CLIENT_ERROR_CODES.CLIENT_NOT_FOUND;
        }
        break;
      case 'TAG_NOT_FOUND':
        return CLIENT_ERROR_CODES.TAG_NOT_FOUND;
      case 'TAG_ALREADY_EXISTS':
      case 'DUPLICATE_TAG':
        return CLIENT_ERROR_CODES.TAG_ALREADY_ASSIGNED;
      default:
        break;
    }
  }

  // Map by HTTP status code
  switch (status) {
    case 400:
      return CLIENT_ERROR_CODES.UNKNOWN;
    case 401:
      return CLIENT_ERROR_CODES.UNAUTHORIZED;
    case 403:
      return CLIENT_ERROR_CODES.FORBIDDEN;
    case 404:
      // Try to determine what wasn't found based on URL
      if (error.config?.url?.includes('/tags/')) {
        return CLIENT_ERROR_CODES.TAG_NOT_FOUND;
      }
      if (error.config?.url?.includes('/relationships/')) {
        return CLIENT_ERROR_CODES.RELATIONSHIP_NOT_FOUND;
      }
      return CLIENT_ERROR_CODES.CLIENT_NOT_FOUND;
    case 408:
      return CLIENT_ERROR_CODES.TIMEOUT;
    case 409:
      // Conflict - often duplicate
      if (error.config?.url?.includes('/tags')) {
        return CLIENT_ERROR_CODES.TAG_ALREADY_ASSIGNED;
      }
      return CLIENT_ERROR_CODES.UNKNOWN;
    default:
      return CLIENT_ERROR_CODES.UNKNOWN;
  }
};

/**
 * Extracts a user-friendly error message from an API error
 * @param {Error} error - Axios error object
 * @param {string} fallbackMessage - Optional fallback message
 * @returns {string} User-friendly error message
 */
export const getClientErrorMessage = (error, fallbackMessage = null) => {
  const errorCode = mapErrorToCode(error);
  const standardMessage = ERROR_MESSAGES[errorCode];

  // If API provided a specific message, use it for certain error types
  const apiMessage = error.response?.data?.message || error.response?.data?.error?.message;
  if (apiMessage && errorCode !== CLIENT_ERROR_CODES.UNKNOWN) {
    return apiMessage;
  }

  return fallbackMessage || standardMessage || ERROR_MESSAGES[CLIENT_ERROR_CODES.UNKNOWN];
};

/**
 * Creates a standardized error object from an API error
 * @param {Error} error - Axios error object
 * @returns {Object} Standardized error object
 */
export const createClientError = error => {
  const code = mapErrorToCode(error);
  return {
    code,
    message: getClientErrorMessage(error),
    originalError: error,
    status: error.response?.status || null,
  };
};

/**
 * Determines if an error is retryable (for retry logic)
 * @param {Error} error - Axios error object
 * @returns {boolean} Whether the error is retryable
 */
export const isRetryableError = error => {
  const code = mapErrorToCode(error);

  // Network and timeout errors are retryable
  if (code === CLIENT_ERROR_CODES.NETWORK || code === CLIENT_ERROR_CODES.TIMEOUT) {
    return true;
  }

  // Server errors (5xx) are retryable
  const status = error.response?.status;
  if (status && status >= 500 && status < 600) {
    return true;
  }

  return false;
};
